import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import companyService from '../../../services/companyService';

const InvoicePreview = ({ isOpen, onClose, invoice }) => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCompanyInfo();
    }
  }, [isOpen]);

  const loadCompanyInfo = async () => {
    try {
      setLoading(true);
      const result = await companyService.getCompanyInfo();
      if (result?.success) {
        setCompanyInfo(result.data);
      }
    } catch (error) {
      console.error('Error loading company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handlePrint = () => {
    // Make print container visible and positioned correctly
    const printContainer = document.querySelector('.invoice-print-container');
    if (printContainer) {
      printContainer.style.position = 'fixed';
      printContainer.style.left = '0';
      printContainer.style.top = '0';
      printContainer.style.width = '100%';
      printContainer.style.zIndex = '999999';
      printContainer.style.background = 'white';
    }
    
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      window.print();
      
      // Reset after print
      setTimeout(() => {
        if (printContainer) {
          printContainer.style.position = 'absolute';
          printContainer.style.left = '-9999px';
          printContainer.style.top = '0';
          printContainer.style.width = '210mm';
          printContainer.style.zIndex = 'auto';
          printContainer.style.background = '';
        }
      }, 100);
    }, 50);
  };

  const getLogoUrl = () => {
    if (!companyInfo?.logo_url) return null;
    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`;
    return `${baseUrl}${companyInfo.logo_url}`;
  };

  if (!isOpen || !invoice) return null;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1cm;
            size: A4;
          }
          
          /* Hide all body content except invoice print container */
          body * {
            visibility: hidden;
          }
          
          /* Show invoice print container and all its children */
          .invoice-print-container,
          .invoice-print-container * {
            visibility: visible !important;
          }
          
          /* Position print container for printing */
          .invoice-print-container {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            background: white !important;
            z-index: 999999 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Hide non-print elements */
          .no-print,
          .no-print * {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Style invoice content for print */
          .invoice-content-wrapper {
            position: relative;
            background: white !important;
            color: #000 !important;
            padding: 20px !important;
            margin: 0 !important;
            width: 100%;
            max-width: 100%;
            display: block !important;
          }
          
          .invoice-content-wrapper * {
            color: #000 !important;
            background: transparent !important;
          }
          
          .invoice-content-wrapper img {
            max-width: 200px !important;
            height: auto !important;
            display: block !important;
          }
          
          .invoice-content-wrapper table {
            border-collapse: collapse !important;
            width: 100% !important;
            page-break-inside: avoid;
            display: table !important;
          }
          
          .invoice-content-wrapper th,
          .invoice-content-wrapper td {
            border: 1px solid #000 !important;
            padding: 8px !important;
            display: table-cell !important;
          }
          
          .invoice-content-wrapper tr {
            display: table-row !important;
          }
          
          .invoice-content-wrapper thead {
            display: table-header-group !important;
          }
          
          .invoice-content-wrapper tbody {
            display: table-row-group !important;
          }
        }
      `}</style>

      {/* Print Container (always in DOM, hidden visually until print) */}
      <div className="invoice-print-container" style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm' }}>
        <div className="invoice-content-wrapper">
          <InvoiceContent invoice={invoice} companyInfo={companyInfo} loading={loading} getLogoUrl={getLogoUrl} formatCurrency={formatCurrency} formatDate={formatDate} />
        </div>
      </div>

      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print">
        <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-border no-print">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Invoice Preview</h2>
                <p className="text-sm text-muted-foreground">Review before printing</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handlePrint}>
                  <Icon name="Printer" size={16} className="mr-2" />
                  Print
                </Button>
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
          </div>

          {/* Preview Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-100px)] p-6">
            <div className="invoice-content-wrapper">
              <InvoiceContent invoice={invoice} companyInfo={companyInfo} loading={loading} getLogoUrl={getLogoUrl} formatCurrency={formatCurrency} formatDate={formatDate} />
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

// Separate component for invoice content (used in both modal and print)
const InvoiceContent = ({ invoice, companyInfo, loading, getLogoUrl, formatCurrency, formatDate }) => {
  const logoUrl = getLogoUrl();

  return (
    <div className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Company Header */}
      <div className="border-b-2 border-gray-300 pb-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="Company Logo" 
                className="h-16 mb-4 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            {loading ? (
              <div className="text-sm text-gray-500">Loading company information...</div>
            ) : companyInfo ? (
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{companyInfo.name || 'Company Name'}</h1>
                <div className="text-sm text-gray-600 space-y-1">
                  {companyInfo.address && <div>{companyInfo.address}</div>}
                  <div>
                    {[companyInfo.city, companyInfo.state, companyInfo.pincode].filter(Boolean).join(', ')}
                    {companyInfo.country && companyInfo.country !== 'India' && `, ${companyInfo.country}`}
                  </div>
                  {companyInfo.phone && <div>Phone: {companyInfo.phone}</div>}
                  {companyInfo.email && <div>Email: {companyInfo.email}</div>}
                  {companyInfo.website && <div>Website: {companyInfo.website}</div>}
                  {companyInfo.tax_id && <div className="mt-2 font-medium">GSTIN: {companyInfo.tax_id}</div>}
                </div>
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Company Name</h1>
                <div className="text-sm text-gray-500">Please configure company information in Company Config → Company Info</div>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-gray-900 mb-2">INVOICE</div>
            <div className="text-sm text-gray-600">
              <div>Invoice #: <span className="font-semibold">{invoice.invoiceNumber || invoice.invoice_number}</span></div>
              <div>Date: {formatDate(invoice.invoiceDate || invoice.issue_date)}</div>
              <div>Due Date: {formatDate(invoice.dueDate || invoice.due_date)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h2>
        <div className="bg-gray-50 p-4 rounded border border-gray-200">
          <div className="font-semibold text-gray-900">{invoice.clientName || invoice.client_name}</div>
          {invoice.clientEmail && (
            <div className="text-sm text-gray-600 mt-1">{invoice.clientEmail || invoice.client_email}</div>
          )}
          {invoice.clientGst && (
            <div className="text-sm text-gray-600 mt-1">GSTIN: {invoice.clientGst}</div>
          )}
        </div>
      </div>

      {/* Services Table */}
      <div className="mb-6">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
              <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-gray-900">SAC Code</th>
              <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold text-gray-900">Quantity</th>
              <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold text-gray-900">Rate</th>
              <th className="border border-gray-300 px-4 py-3 text-right text-sm font-semibold text-gray-900">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Parse services from invoice - handle both array and JSON string
              let services = [];
              if (invoice.services) {
                if (Array.isArray(invoice.services)) {
                  services = invoice.services;
                } else if (typeof invoice.services === 'string') {
                  try {
                    services = JSON.parse(invoice.services);
                  } catch (e) {
                    console.warn('Failed to parse services:', e);
                  }
                }
              }
              
              // If no services, create default from invoice data
              if (!services || services.length === 0) {
                services = [{
                  description: invoice.description || 'Service',
                  sacCode: invoice.sacCode || invoice.sac_code || '',
                  quantity: 1,
                  rate: invoice.amount || (invoice.totalAmount ? invoice.totalAmount / 1.18 : 0),
                  amount: invoice.amount || (invoice.totalAmount ? invoice.totalAmount / 1.18 : 0)
                }];
              }
              
              return services.map((service, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-gray-900">
                    {service.description || 'Service'}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-center text-sm text-gray-600">
                    {service.sacCode || service.sac_code || '-'}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-right text-sm text-gray-900">
                    {service.quantity || 1}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-right text-sm text-gray-900">
                    {formatCurrency(service.rate || 0)}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {formatCurrency(service.amount || (service.rate || 0) * (service.quantity || 1))}
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* Amount Summary */}
      <div className="flex justify-end mb-6">
        <div className="w-80">
          <table className="w-full">
            <tbody>
              <tr>
                <td className="px-4 py-2 text-right text-sm text-gray-600">Subtotal:</td>
                <td className="px-4 py-2 text-right text-sm font-medium text-gray-900">
                  {formatCurrency(invoice.amount || (invoice.totalAmount || invoice.total_amount) / 1.18)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-right text-sm text-gray-600">GST (18%):</td>
                <td className="px-4 py-2 text-right text-sm font-medium text-gray-900">
                  {formatCurrency(invoice.tax_amount || invoice.gstAmount || ((invoice.totalAmount || invoice.total_amount) * 0.18 / 1.18))}
                </td>
              </tr>
              <tr className="border-t-2 border-gray-900">
                <td className="px-4 py-3 text-right text-lg font-bold text-gray-900">Total:</td>
                <td className="px-4 py-3 text-right text-lg font-bold text-gray-900">
                  {formatCurrency(invoice.totalAmount || invoice.total_amount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Notes */}
      {invoice.description && (
        <div className="mt-8 pt-6 border-t border-gray-300">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.description}</p>
        </div>
      )}

      {/* Payment Terms */}
      <div className="mt-8 pt-6 border-t border-gray-300">
        <div className="text-xs text-gray-500 text-center">
          <div>Payment Terms: Net 30 days</div>
          <div className="mt-2">Thank you for your business!</div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;

